// WHAT: Optimized Vascular History Hook
// WHY:  Fetches vascular age history data from backend with date range filtering
// DOMAIN: Health → Oura
// DEPENDENCIES: Tauri core API, debug logging
// RISKS:
//   - NOTE: Hook refetches data whenever startDate or endDate changes
//   - NOTE: No caching mechanism - always fetches from backend
//   - NOTE: Error state is set but not automatically cleared

import { core } from "@tauri-apps/api";
import { useState, useEffect } from "react";

import { debugHealth } from "@/domains/logging/utils/debugLog";

interface VascularAgeHistoryData {
  day: string;
  timestamp: string;
  vascular_age: number; // Backend uses snake_case
}

/**
 * WHAT:
 *   Custom hook for fetching Oura vascular age history data with date range.
 *
 * WHY:
 *   Provides optimized data fetching with loading and error states for vascular age trends.
 *
 * INPUTS:
 *   - startDate: string — Start date for history range (ISO format)
 *   - endDate: string — End date for history range (ISO format)
 *
 * OUTPUT:
 *   - data: VascularAgeHistoryData[] — Array of vascular age data points
 *   - loading: boolean — Loading state
 *   - error: string | null — Error message if fetch failed
 *
 * BEHAVIOR:
 *   - Fetches data on mount and when date range changes
 *   - Skips fetch if startDate or endDate is empty
 *   - Logs fetch operations using debug logging
 *   - Sets loading state during fetch
 *   - Returns empty array and error message on failure
 *
 * RISKS:
 *   - NOTE: No validation of date format (assumes valid ISO strings)
 *   - NOTE: Hook refetches on every date change (no debouncing)
 *   - NOTE: Error state persists until next successful fetch
 *
 * @param startDate - Start date for history range (ISO format)
 *
 * @param endDate - End date for history range (ISO format)
 *
 * @returns Object containing vascular age history data, loading state, and error
 *
 */
export const useOptimizedVascularHistory = (startDate: string, endDate: string) => {
  const [data, setData] = useState<VascularAgeHistoryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const fetchVascularHistory = async () => {
      if (!startDate || !endDate) {
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        debugHealth("info", "VascularAgeHistory", "Fetching vascular age history", {
          endDate,
          module: "useOptimizedVascularHistory",
          startDate,
        });

        const result = await core.invoke<VascularAgeHistoryData[]>("get_oura_vascular_history", {
          request: {
            endDate,
            startDate,
          },
        });

        debugHealth("info", "VascularAgeHistory", "Vascular age history loaded", {
          data: result.slice(0, 3), // Log first 3 entries
          dataCount: result.length,
          module: "useOptimizedVascularHistory",
        });

        setData(result);
      } catch (error_) {
        const errorMessage = error_ instanceof Error ? error_.message : "Unknown error";
        debugHealth("error", "VascularAgeHistory", "Failed to fetch vascular age history", {
          endDate,
          error: errorMessage,
          module: "useOptimizedVascularHistory",
          startDate,
        });
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    void fetchVascularHistory();
  }, [startDate, endDate]);

  return { data, error, loading };
};
